import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingApi, campusApi } from '../services/api';
import { PageHead, Badge, RefCode, Btn, TableWrap, Th, Td, Empty, Spinner, Toast, Modal, Card, Textarea } from '../components/UI';
import { getLastNDaysRange, parseYmd } from '../utils/dateRange';

export default function ApprovalsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialRange = getLastNDaysRange(6);

  const [pending, setPending] = useState([]);
  const [reviewed, setReviewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All');
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null); // { type:'approve'|'reject'|'view', booking }
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const calendarRange = getCalendarRange();
    const [pendingRes, myRes] = await Promise.allSettled([
      bookingApi.getPending(user?.campusID),
      bookingApi.getMy(undefined, undefined, undefined),
    ]);

    let calendarData = [];
    try {
      if (String(user?.roleName || '').toLowerCase() === 'admin') {
        const campusesRes = await campusApi.getAll();
        const campuses = campusesRes?.data?.data || [];
        if (campuses.length > 0) {
          const campusCalls = campuses.map((c) =>
            bookingApi.getCalendar(c.campusID, calendarRange.from, calendarRange.to)
          );
          const settled = await Promise.allSettled(campusCalls);
          calendarData = settled
            .filter((s) => s.status === 'fulfilled')
            .flatMap((s) => s.value?.data?.data || []);
        } else if (user?.campusID) {
          const calendarRes = await bookingApi.getCalendar(user.campusID, calendarRange.from, calendarRange.to);
          calendarData = calendarRes?.data?.data || [];
        }
      } else if (user?.campusID) {
        const calendarRes = await bookingApi.getCalendar(user.campusID, calendarRange.from, calendarRange.to);
        calendarData = calendarRes?.data?.data || [];
      }
    } catch {
      calendarData = [];
    }

    const pendingData = pendingRes.status === 'fulfilled' ? (pendingRes.value.data.data || []) : [];
    const myData = myRes.status === 'fulfilled' ? (myRes.value.data.data || []) : [];

    setPending(pendingData);
    const uniqueCalendar = dedupeByBookingId(calendarData);
    const reviewedFromCalendar = uniqueCalendar.filter((b) => !isPendingStatus(b.status));
    const reviewedFromMine = myData.filter((b) => !isPendingStatus(b.status));
    setReviewed(reviewedFromCalendar.length ? reviewedFromCalendar : reviewedFromMine);

    if (pendingRes.status === 'rejected' && myRes.status === 'rejected' && reviewedFromCalendar.length === 0) {
      setPending(DEMO.filter((b) => b.status === 'Pending'));
      setReviewed(DEMO.filter((b) => b.status !== 'Pending'));
    }

    setLoading(false);
  }

  async function handleReview(action) {
    if (!modal?.booking) return;
    setSubmitting(true);
    try {
      const res = await bookingApi.review(modal.booking.bookingID || modal.booking.id, action, remarks);
      const apiMessage = res?.data?.message;
      const fallback = `Booking LE ${action === 'Approve' ? 'approved' : 'rejected'} successfully.`;
      setToast({ msg: apiMessage || fallback, type: action === 'Approve' ? 'success' : 'error' });
      setModal(null);
      setRemarks('');
      load();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Action failed. Please try again.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  function inDateRange(item) {
    const raw = item?.eventDate || item?.date || item?.submittedAt;
    const itemDate = parseFlexibleDate(raw);
    if (!itemDate) return false;
    const start = parseFlexibleDate(fromDate);
    const end = parseFlexibleDate(toDate);
    if (start && itemDate < start) return false;
    if (end && itemDate > end) return false;
    return true;
  }

  function matchesSearch(item) {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (item?.eventName || item?.event || '').toLowerCase().includes(q)
      || (item?.bookingReference || item?.ref || '').toLowerCase().includes(q)
      || (item?.eventAreaName || item?.area || '').toLowerCase().includes(q)
      || (item?.createdByName || '').toLowerCase().includes(q)
      || (item?.createdBy || item?.by || '').toLowerCase().includes(q)
    );
  }

  const pendingDisplayed = pending
    .filter((b) => inDateRange(b))
    .filter((b) => matchesSearch(b));

  const normalizedStatus = String(status || 'All').trim();
  const reviewedDisplayed = reviewed
    .filter((b) => inDateRange(b))
    .filter((b) => {
      if (normalizedStatus === 'All' || normalizedStatus === 'Pending') return true;
      return String(b.status || '').trim().toLowerCase() === normalizedStatus.toLowerCase();
    })
    .filter((b) => matchesSearch(b));

  const showPendingSection = normalizedStatus === 'All' || normalizedStatus === 'Pending';
  const pendingCount = pendingDisplayed.length;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem', gap: 10 }}>
        <Spinner size={24} />
        <span style={{ color: 'var(--text2)' }}>Loading approvals...</span>
      </div>
    );
  }

  return (
    <div className="page-anim">
      <PageHead
        title="Approval Queue"
        subtitle={showPendingSection
          ? `${pendingCount} Booking LE awaiting review`
          : `${reviewedDisplayed.length} ${status} Booking LE record(s)`}
      />

      <Card style={{ marginBottom: '1rem', padding: '1rem 1.2rem' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ padding: '7px 10px', fontSize: 13, minWidth: 140, border: '1.5px solid var(--border)', borderRadius: 8, outline: 'none', fontFamily: 'var(--font-body)' }}
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ padding: '7px 10px', fontSize: 13, minWidth: 140, border: '1.5px solid var(--border)', borderRadius: 8, outline: 'none', fontFamily: 'var(--font-body)' }}
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ padding: '7px 12px', fontSize: 13, border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', outline: 'none', background: 'white' }}
          >
            <option value="All">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search event, reference, area, requester..."
            style={{ padding: '7px 12px', fontSize: 13, flex: 1, minWidth: 180, border: '1.5px solid var(--border)', borderRadius: 8, outline: 'none', fontFamily: 'var(--font-body)' }}
          />
          <Btn
            size="sm"
            variant="ghost"
            onClick={() => {
              const range = getLastNDaysRange(6);
              setSearch('');
              setStatus('All');
              setFromDate(range.from);
              setToDate(range.to);
            }}
          >
            Reset
          </Btn>
        </div>
      </Card>

      {showPendingSection && (pendingDisplayed.length === 0 ? (
        <Card><Empty icon="[]" text="All caught up! No pending approvals." /></Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
          {pendingDisplayed.map((b) => (
            <Card key={b.bookingID || b.id}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-head)', fontSize: 16, fontWeight: 700 }}>{b.eventName}</div>
                  <RefCode value={b.bookingReference || b.ref} />
                </div>
                <Badge status="Pending" />
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, color: 'var(--text2)', marginBottom: 14 }}>
                {[
                  ['Area', b.eventAreaName || b.area],
                  ['Date', b.eventDate || b.date],
                  ['Time', `${b.startTime || b.s} - ${b.endTime || b.e}`],
                  ['Attendees', `${b.expectedAttendees || b.att || 0}`],
                  ['Requested By', b.createdBy || b.by],
                  ['Campus', b.campusName || b.campus],
                  ['Slots', `${b.totalSlots || b.slots}`],
                ].map(([label, val]) => (
                  <span key={`${label}-${val}`} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--bg2)', padding: '3px 9px', borderRadius: 20 }}>
                    <strong>{label}:</strong> {val}
                  </span>
                ))}
              </div>

              {b.purpose && (
                <div style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--bg2)', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>
                  {b.purpose}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn variant="green" onClick={() => { setModal({ type: 'approve', booking: b }); setRemarks(''); }}>Approve</Btn>
                <Btn variant="red" onClick={() => { setModal({ type: 'reject', booking: b }); setRemarks(''); }}>Reject</Btn>
                <Btn variant="ghost" size="sm" onClick={() => setModal({ type: 'view', booking: b })}>View Details</Btn>
              </div>
            </Card>
          ))}
        </div>
      ))}

      {reviewedDisplayed.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text2)', marginBottom: '1rem' }}>Recently Reviewed</div>
          <TableWrap>
            <thead>
              <tr>
                <Th>Reference</Th><Th>Event</Th><Th>Date</Th><Th>Area</Th><Th>Status</Th><Th>Remarks</Th><Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {reviewedDisplayed.map((b) => (
                <tr key={b.bookingID || b.id}>
                  <Td><RefCode value={b.bookingReference || b.ref} /></Td>
                  <Td style={{ fontWeight: 600 }}>{b.eventName || b.event}</Td>
                  <Td>{b.eventDate || b.date}</Td>
                  <Td>{b.eventAreaName || b.area}</Td>
                  <Td><Badge status={b.status} /></Td>
                  <Td style={{ color: 'var(--text2)', fontSize: 12 }}>{b.reviewRemarks || '-'}</Td>
                  <Td>
                    {isAdminUser(user) && isReviewedStatus(b.status) ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {!isStatus(b.status, 'Approved') && (
                          <Btn size="sm" variant="green" onClick={() => { setModal({ type: 'approve', booking: b }); setRemarks(''); }}>
                            Mark Approved
                          </Btn>
                        )}
                        {!isStatus(b.status, 'Rejected') && (
                          <Btn size="sm" variant="red" onClick={() => { setModal({ type: 'reject', booking: b }); setRemarks(''); }}>
                            Mark Rejected
                          </Btn>
                        )}
                      </div>
                    ) : '-'}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </>
      )}

      {modal?.type === 'approve' && (
        <Modal title="Approve Booking LE" onClose={() => setModal(null)}>
          <BookingSummaryBlock b={modal.booking} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1.2rem' }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase' }}>Remarks (optional)</label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Any instructions or notes..." speechToText style={{ minHeight: 140 }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="green" onClick={() => handleReview('Approve')} disabled={submitting} style={{ flex: 1, justifyContent: 'center' }}>
              {submitting ? <Spinner size={16} color="white" /> : 'Confirm Approval'}
            </Btn>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {modal?.type === 'reject' && (
        <Modal title="Reject Booking LE" onClose={() => setModal(null)}>
          <BookingSummaryBlock b={modal.booking} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1.2rem' }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase' }}>Rejection Reason *</label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Please state the reason for rejection..." speechToText style={{ minHeight: 150 }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="red" onClick={() => handleReview('Reject')} disabled={submitting || !remarks.trim()} style={{ flex: 1, justifyContent: 'center' }}>
              {submitting ? <Spinner size={16} color="var(--red)" /> : 'Confirm Rejection'}
            </Btn>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {modal?.type === 'view' && (
        <Modal title="Booking LE Details" onClose={() => setModal(null)}>
          <BookingDetailGrid b={modal.booking} />
          <div style={{ display: 'flex', gap: 10, marginTop: '1.2rem', flexWrap: 'wrap' }}>
            {isPendingStatus(modal.booking.status) && (
              <>
                <Btn variant="green" onClick={() => setModal({ type: 'approve', booking: modal.booking })}>Approve</Btn>
                <Btn variant="red" onClick={() => setModal({ type: 'reject', booking: modal.booking })}>Reject</Btn>
              </>
            )}
            <Btn variant="ghost" onClick={() => setModal(null)}>Close</Btn>
          </div>
        </Modal>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function BookingSummaryBlock({ b }) {
  return (
    <div style={{ padding: '12px 14px', background: 'var(--orange-light)', borderRadius: 8, marginBottom: '1rem' }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{b.eventName || b.event}</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
        {b.eventDate || b.date} | {b.startTime || b.s} - {b.endTime || b.e} | {b.eventAreaName || b.area}
      </div>
    </div>
  );
}

function BookingDetailGrid({ b }) {
  const rows = [
    ['Reference', b.bookingReference || b.ref],
    ['Status', b.status],
    ['Event Name', b.eventName || b.event],
    ['Campus', b.campusName || b.campus],
    ['Event Area', b.eventAreaName || b.area],
    ['Date', b.eventDate || b.date],
    ['Time', `${b.startTime || b.s} - ${b.endTime || b.e}`],
    ['Slots', b.totalSlots || b.slots],
    ['Attendees', b.expectedAttendees || b.att || '-'],
    ['Requested By', b.createdBy || b.by],
    ['Purpose', b.purpose || '-'],
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      {rows.map(([label, val]) => (
        <div key={label} style={{ padding: '10px', background: 'var(--bg2)', borderRadius: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {label === 'Status' ? <Badge status={val} /> : val}
          </div>
        </div>
      ))}
    </div>
  );
}

const DEMO = [
  { id: 1, bookingID: 1, bookingReference: 'CHW-20260601-0001', eventName: 'Annual Sports Day', eventAreaName: 'Multi-Purpose Hall', campusName: 'Chinchwad', eventDate: '2026-06-10', startTime: '08:00', endTime: '11:00', totalSlots: 3, status: 'Approved', createdBy: 'Priya Sharma', expectedAttendees: 300, reviewRemarks: 'Approved. Setup by 7:45 AM.' },
  { id: 2, bookingID: 2, bookingReference: 'CHW-20260601-0002', eventName: 'Science Exhibition', eventAreaName: 'Conference Room A', campusName: 'Chinchwad', eventDate: '2026-06-12', startTime: '09:00', endTime: '12:00', totalSlots: 3, status: 'Pending', createdBy: 'Rahul Mehta', expectedAttendees: 80, purpose: 'Annual science fair for grades 6-10' },
  { id: 3, bookingID: 3, bookingReference: 'HJW-20260601-0001', eventName: 'Parent-Teacher Meet', eventAreaName: 'Seminar Hall', campusName: 'Hinjawadi', eventDate: '2026-06-15', startTime: '14:00', endTime: '17:00', totalSlots: 3, status: 'Pending', createdBy: 'Anjali Patel', expectedAttendees: 120, purpose: 'Term 1 parent-teacher interaction' },
  { id: 4, bookingID: 4, bookingReference: 'CHW-20260601-0003', eventName: 'Robotics Workshop', eventAreaName: 'Computer Lab', campusName: 'Chinchwad', eventDate: '2026-06-18', startTime: '10:00', endTime: '13:00', totalSlots: 3, status: 'Approved', createdBy: 'Suresh Kumar', expectedAttendees: 40 },
  { id: 5, bookingID: 5, bookingReference: 'CHW-20260601-0004', eventName: 'Staff Meeting', eventAreaName: 'Conference Room B', campusName: 'Chinchwad', eventDate: '2026-06-20', startTime: '15:00', endTime: '16:00', totalSlots: 1, status: 'Rejected', createdBy: 'Meena Singh', expectedAttendees: 20, reviewRemarks: 'Already booked for Board meeting.' },
  { id: 6, bookingID: 6, bookingReference: 'HJW-20260601-0002', eventName: 'Cultural Programme', eventAreaName: 'Multi-Purpose Hall', campusName: 'Hinjawadi', eventDate: '2026-06-25', startTime: '16:00', endTime: '20:00', totalSlots: 4, status: 'Pending', createdBy: 'Dev Rathi', expectedAttendees: 350, purpose: 'Annual day cultural event' },
];

function isPendingStatus(status) {
  return isStatus(status, 'Pending');
}

function isReviewedStatus(status) {
  return isStatus(status, 'Approved') || isStatus(status, 'Rejected');
}

function isStatus(status, expected) {
  return String(status || '').trim().toLowerCase() === String(expected || '').trim().toLowerCase();
}

function isAdminUser(user) {
  return String(user?.roleName || '').trim().toLowerCase() === 'admin';
}

function dedupeByBookingId(rows) {
  const seen = new Set();
  const out = [];
  (rows || []).forEach((row) => {
    const id = row?.bookingID || row?.bookingId || row?.id || row?.bookingReference;
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(row);
  });
  return out;
}

function getCalendarRange() {
  const now = new Date();
  const from = new Date(now);
  from.setDate(from.getDate() - 120);
  const to = new Date(now);
  to.setDate(to.getDate() + 120);
  return {
    from: toYmd(from),
    to: toYmd(to),
  };
}

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseFlexibleDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const ymd = parseYmd(raw);
  if (ymd) return ymd;

  // Support dd-MM-yyyy input too.
  const m = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return Number.isNaN(d.getTime()) ? null : d;
}
